import React, {useState, useEffect} from 'react';
import TabContentWrapper from '../TabContentWrapper';
import {ControlButton, Glyphs, TscButtonType, TscButtonGlyphPlacement} from '@techsmith/tsc-cloud-style-guide';
import {useTranslation} from 'react-i18next';
import dayjs from 'dayjs';
import DataTable from '../DataTable';
import {IConversationData} from '../../../model/conversationModel';
import TabHeader from '../TabHeader';
import CreateConversationModal from '../../modals/CreateConversationModal';
import {IWorkerState} from '../../../model/workerState';
import {HideButtonText} from '../../util/StyledElements';
import cssConstants from '../../../constants/cssConstants';
import {conversationsStore, updateConversationsFromServer} from '../../../context/conversationsProvider';
import withMemoizedContexts from '../../../context/contextContainerHoC';
import ConversationActionsButtonGroup from './ConversationActionsButtonGroup';
import styled from 'styled-components';
import {UpsertQueryParamInUrl} from '../../util/QueryParamsHelper';
import Constants from '../../../constants/Constants';
import {useQueryParam, StringParam} from 'use-query-params';
import {useTscTheme} from '../../../context/themeProvider';
import {mediaStore} from '../../../context/mediaProvider';

const DataTableWrapper = styled.div`
   .data-table-row, .data-table-header {
      grid-template-columns: 2fr repeat(2,0.75fr) 1.25fr 13rem;
      grid-gap: 10px;
   }

   ul {
      grid-template-columns: repeat( auto-fit, minmax(19rem, 1fr) );
   }
`;

export const ConversationsTabBase: React.FC<IWorkerState & IStateMappedProps> = ({conversations, isWorking: initialIsWorking, hasError: initialHasError, Hash}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [showCreateConversationModal, setShowCreateConversationModal] = useState(false);
   const [conversationParam] = useQueryParam(Constants.queryParams.conversation, StringParam);
   const [loadingState, setLoadingState] = useState({isWorking: initialIsWorking, hasError: initialHasError});

   const getConversations = async () => {
      try {
         conversations || setLoadingState({isWorking: true, hasError: false});
         await updateConversationsFromServer(Hash);
         setLoadingState({isWorking: false, hasError: false});
      } catch {
         setLoadingState({isWorking: false, hasError: true});
      }
   };

   useEffect(() => {
      void getConversations();
   }, []);

   useEffect(() => {
      void (!conversations?.findIndex(conversation => conversation.VideoReviewId === conversationParam) && getConversations());
   }, [conversationParam]);

   const onCreateConversationButtonClicked = () => {
      setShowCreateConversationModal(true);
   };

   const onDownloadError = () => {
      setLoadingState({isWorking: false, hasError: true});
   };

   const conversationColumnLabels = [
      t('conversations.columnLabels.title'),
      t('conversations.columnLabels.comments'),
      t('conversations.columnLabels.participants'),
      t('conversations.columnLabels.created'),
      t('conversations.columnLabels.actions')
   ];

   const sortedConversations = conversations?.sort((a, b) => a.Title.toLowerCase() > b.Title.toLowerCase() ? 1 : -1) || [];

   return (
      <TabContentWrapper
         showSpinner={loadingState.isWorking}
         showError={loadingState.hasError}
      >
         <TabHeader
            leftLabel={t('conversations.tabName')}
            rightLabel=""
            hideRightLabelInMobileView={true}
            button={(
               <HideButtonText
                  breakpointWidth={cssConstants.contentBreakpoint}
                  glyphPlacement={TscButtonGlyphPlacement.left}
               >
                  <ControlButton
                     onClick={onCreateConversationButtonClicked}
                     buttonType={TscButtonType.secondary}
                     themeName={theme}
                     glyph={<Glyphs.Add16x16 />}
                     glyphPlacement={TscButtonGlyphPlacement.left}
                     title={t('conversations.addConversationTitle')}
                     label={t('conversations.addConversationTitle')}
                     aria={{'aria-label': t('conversations.addConversationTitle')}}
                     testId="create-conversation-button"
                  />
               </HideButtonText>
            )}
         />
         <DataTableWrapper data-test-id="conversations-list">
            <DataTable
               columnLabels={conversationColumnLabels}
               emptyStateLabel={t('conversations.emptyStateString')}
               tableRows={sortedConversations.map((conversation: IConversationData) => ({
                  tableRow: [
                     conversation.Title,
                     conversation.CommentCount,
                     conversation.CommenterCount,
                     dayjs(conversation.DateCreated).format('MMMM D, YYYY'),
                     <ConversationActionsButtonGroup
                        conversation={conversation}
                        onDownloadError={onDownloadError}
                        key={conversation.VideoReviewId}
                     />
                  ],
                  link: UpsertQueryParamInUrl(window.location.href, Constants.queryParams.conversation, conversation.VideoReviewId)
               }))}
            />
         </DataTableWrapper>
         {showCreateConversationModal && <CreateConversationModal onClose={() => setShowCreateConversationModal(false)} />}
      </TabContentWrapper>
   );
};

export interface IStateMappedProps {
   conversations: IConversationData[];
   Hash: string;
}

const mapStatesToProps = (
   {isWorking, hasError}: IWorkerState,
   {conversations}: Partial<IStateMappedProps>,
   {Hash}: Partial<IStateMappedProps>
): IWorkerState & IStateMappedProps => ({conversations, isWorking, hasError, Hash});

export default withMemoizedContexts(mapStatesToProps, conversationsStore, mediaStore)(ConversationsTabBase);
